import { isEmpty, isEqual, orderBy } from 'lodash'
import { useState, useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { Container, Row, Col, Button } from 'reactstrap'
import { resolveAddresses } from '../../util/utils'
import LoadingSpinner from '../general/LoadingSpinner'
import FormAddressWrapper from '../Shop/DigitalProofingForms/FormAddressWrapper'
import CheckoutAddressPanel from './CheckoutAddressPanel'
import {
  setCustomerAddress,
  setBillingAddress,
  clearGuestAddresses,
  setMultiAddressSelection
} from './checkoutFunctions'
import NewAddressForm from './NewAddressForm'

const CheckoutAddressSelection = props => {
  const [newAddressMode, setNewAddressMode] = useState(false)
  const [manageAddresses, setManageAddresses] = useState(false)
  const lastShippingAddressRef = useRef(null)
  const lastBillingAddressRef = useRef(null)
  const sessionTokenAddressResolvedRef = useRef(false)
  const currentUserId = props.currentUser.id
  const orderById = (adds, order = 'desc') => {
    return orderBy(adds, 'id', order)
  }

  // Track last non-empty addresses for guest users (no useEffect needed - just update refs during render)
  if (!props.currentUser.id && !isEmpty(props.selectedCustomerAddress)) {
    lastShippingAddressRef.current = props.selectedCustomerAddress
  }
  if (!props.currentUser.id && !isEmpty(props.selectedBillingAddress)) {
    lastBillingAddressRef.current = props.selectedBillingAddress
  }

  // when user logs in, clear any guest address selections
  useEffect(() => {
    if (currentUserId) {
      props.clearGuestAddresses()
      // clear main address selection if guest address
      const selectedAddressId = props.selectedCustomerAddress?.id
      const guestAddressSelected =
        typeof selectedAddressId === 'string' ? selectedAddressId.startsWith('guest-') : false
      if (guestAddressSelected) {
        props.setCustomerAddress({})
      }

      // do the same for any multi-address selections
      const multiAddressSelections = props.multiAddressSelection
      if (multiAddressSelections) {
        Object.values(multiAddressSelections).forEach(selection => {
          if (typeof selection.address?.id === 'string' && selection.address.id.startsWith('guest-')) {
            setMultiAddressSelection(selection.cartItemId, {
              address: {},
              quantity: selection.quantity
            })
          }
        })
      }
    }
  }, [currentUserId])

  // For guest users, reset newAddressMode when shipping address is deselected
  useEffect(() => {
    if (!currentUserId && isEmpty(props.selectedCustomerAddress)) {
      setNewAddressMode(false)
    }
  }, [props.selectedCustomerAddress, currentUserId])

  // Resolve checkout session token customer address when addresses become available
  // (handles race condition where session token loads before addresses on refresh)
  useEffect(() => {
    // Skip if no pending ID, already resolved, or addresses not loaded yet
    if (!props.sessionTokenAddressId || sessionTokenAddressResolvedRef.current) return

    const allAddresses = [...(props.customer_addresses ?? []), ...Object.values(props.managed_customer_addresses ?? {})]

    if (allAddresses.length === 0) return

    const address = allAddresses.find(a => a.id === props.sessionTokenAddressId)
    if (address) {
      sessionTokenAddressResolvedRef.current = true
      props.setCustomerAddress(address)
    }
  }, [
    props.sessionTokenAddressId,
    props.customer_addresses,
    props.managed_customer_addresses,
    props.setCustomerAddress
  ])

  // Hide shipping address if all items are Digital customer
  const allItemsDigitalDelivery = useMemo(
    () => props.cartItems.every(cartItem => cartItem.shippable_type === 'Digital Delivery'),
    [props.cartItems]
  )

  // after cart is loaded, check for default address
  useEffect(() => {
    if (props.cart_loading === false) {
      // Skip if an address was already restored from session token or session token address is resolved
      if (!isEmpty(props.selectedCustomerAddress) || sessionTokenAddressResolvedRef.current) {
        sessionTokenAddressResolvedRef.current = true
        return
      }

      const defaultAddress = props.customer_addresses.find(address => address.default_shipping === true)

      if (defaultAddress) {
        const isSysMangNotAddressBook = defaultAddress.is_system_managed && !props.portal?.has_address_managed
        const isNotSysMangAddressBook =
          !defaultAddress.is_system_managed &&
          props.portal?.has_address_managed &&
          (!props.portal?.has_address_standard || props.customerGroup.disable_hybrid_shipping)

        if (!(isSysMangNotAddressBook || isNotSysMangAddressBook)) {
          // Always set the default billing address if found and conditions met
          props.setBillingAddress(defaultAddress)
          // Only set the default shipping address if NOT all items are digital
          if (!allItemsDigitalDelivery) {
            props.setCustomerAddress(defaultAddress)
          }
        }
      }
    }
  }, [
    props.cart_loading,
    props.customer_addresses,
    props.portal,
    props.customerGroup.disable_hybrid_shipping,
    allItemsDigitalDelivery,
    props.setBillingAddress,
    props.setCustomerAddress
  ])

  // watch for managed/trad address updates and consolidate them into a single array
  const shippingAddresses = useMemo(() => {
    // resolve which addresses should go to list
    const traditionalAddresses = props.customer_addresses
    const managedAddresses = Object.values(props.managed_customer_addresses)
    const guestAddresses = props.guestAddresses || []

    let addressesForList = []

    addressesForList = resolveAddresses([...traditionalAddresses, ...guestAddresses], managedAddresses, props.portal)

    const orderAddresses = orderById(addressesForList)
    return orderAddresses
  }, [props.customer_addresses, props.managed_customer_addresses, props.portal, props.guestAddresses])

  // if all items are digital, we must always show billing because there is no shipping address
  const hideShippingAddress = allItemsDigitalDelivery

  if (props.cart_loading) return <LoadingSpinner />

  return (
    <div className="box-shadow white-background m-0">
      {hideShippingAddress ? null : (
        <>
          <div className="ckout-header">
            <h5 className="m-0">{newAddressMode === true ? 'Add New ' : 'Shipping '} Address</h5>
          </div>
          <Container>
            <Row>
              <Col>
                {/* For guest users, show form when no address is selected OR when newAddressMode is true */}
                {!props.currentUser.id && (isEmpty(props.selectedCustomerAddress) || newAddressMode) ? (
                  <NewAddressForm
                    newAddressMode={newAddressMode}
                    setNewAddressMode={setNewAddressMode}
                    afterCreateAddress={props.setCustomerAddress}
                    initialAddress={
                      !isEmpty(props.selectedCustomerAddress)
                        ? props.selectedCustomerAddress
                        : lastShippingAddressRef.current
                    }
                  />
                ) : /* For registered users, show form when no addresses exist and portal supports standard addresses */
                shippingAddresses.length === 0 && props.portal.has_address_standard ? (
                  <NewAddressForm
                    newAddressMode={newAddressMode}
                    setNewAddressMode={setNewAddressMode}
                    afterCreateAddress={props.setCustomerAddress}
                  />
                ) : (
                  <CheckoutAddressPanel
                    addressType="delivery"
                    addresses={shippingAddresses}
                    newAddressMode={newAddressMode}
                    manageAddresses={manageAddresses}
                  />
                )}
                {/* Don't show address management for guest users */}
                {props.currentUser.id && (
                  <FormAddressWrapper
                    manageAddresses={manageAddresses}
                    setManageAddresses={setManageAddresses}
                    addressType="delivery"
                    afterCreateAddress={props.setCustomerAddress}
                  />
                )}
              </Col>
            </Row>
          </Container>
        </>
      )}
    </div>
  )
}

const mapStateToProps = state => {
  return {
    portal: state.portal,
    currentLocation: state.storeViews.current,
    customer_addresses: state.customer_addresses,
    managed_customer_addresses: state.managed_customer_addresses,
    selectedBillingAddress: state.checkout.selectedBillingAddress,
    selectedCustomerAddress: state.checkout.selectedCustomerAddress,
    sessionTokenAddressId: state.checkout.sessionTokenAddressId,
    cart_loading: state.checkout.cart_loading,
    customerGroup: state.customerGroup,
    cartItems: state.cartItems,
    currentUser: state.currentUser,
    requireBillingAddressForFreeOrders: state.storeViews.current.require_billing_address_for_free_orders,
    customerAddressesLoading: state.loading.customerAddresses,
    useShippingAsBilling: state.checkout.useShippingAsBilling,
    guestAddresses: state.checkout.guestAddresses,
    youPay: state.checkout.youPay,
    multiAddressSelection: state.checkout.multiAddressSelection
  }
}

export default connect(mapStateToProps, {
  setCustomerAddress,
  setBillingAddress,
  clearGuestAddresses
})(CheckoutAddressSelection)
